import React from 'react';
import { Card, Result } from '../../exports';
import { getString } from '@nackle/intl-tools';

/**
 * Error boundary component that catches JavaScript errors in child components
 * and displays a fallback UI instead of crashing the entire application.
 * 
 * @class NackleErrorBoundary
 * @extends {React.Component}
 */
class NackleErrorBoundary extends React.Component {
    /**
     * Creates an instance of NackleErrorBoundary.
     * 
     * @param {Object} props - Component props
     * @param {string} [props.className] - Additional CSS class name
     * @param {string} [props.resultSize] - result size - small or large
     * @param {boolean} [props.hasError] - External error state
     * @param {Function} [props.onError] - Callback function called when an error is caught
     * @param {React.ReactNode} props.children - Child components to wrap
     */
    constructor( props ) {
        super( props );
        this.state = { hasError: false };
    }

    /**
     * Static method called when an error is thrown in a child component.
     * Updates state to indicate an error has occurred.
     * 
     * @static
     * @param {Error} error - The error that was thrown
     * @returns {Object} New state with hasError set to true
     */
    static getDerivedStateFromError( error ) {
        return { hasError: true };
    }
    /**
     * Static method called before rendering to update state based on props.
     * Checks for external error conditions and updates state accordingly.
     * 
     * @static
     * @param {Object} props - Current props
     * @param {Object} state - Current state
     * @returns {Object} Updated state
     */
    static getDerivedStateFromProps( props, state ) {
        if ( props.hasError && !state.hasError ) {
            state.hasError = true;
        }
        return state;
    }

    /**
     * Lifecycle method called after an error has been thrown by a descendant component.
     * Used for error reporting and logging.
     * 
     * @param {Error} error - The error that was thrown
     * @param {Object} errorInfo - Object with componentStack key containing information about component stack
     */
    componentDidCatch( error, errorInfo ) {
        const { onError } = this.props;
        if ( typeof onError === 'function' ) {
            onError( error, errorInfo );
        }
    }
    /**
     * Renders either the error fallback UI or the child components.
     * 
     * @returns {React.ReactElement} Error UI or children components
     */
    render() {
        const { className, resultSize } = this.props;
        if ( this.state.hasError ) {
            return (
                <Card variant='borderless' className={ className ? `${ className } error-boundary-container` : 'error-boundary-container' }>
                    <Result size={ ( resultSize === 'large' ? 'default' : resultSize ) || 'default' } title={ getString( 'origami.error.title' ) } type="image" status="error" subTitle={ getString( 'origami.error.message' ) } />
                </Card>
            );
        }

        return this.props.children;
    }
}
export { NackleErrorBoundary };